import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "areaRow",
    "extrasList",
    "extraTemplate",
    "modal",
    "customizeButton",
  ];
  static values = { printUrl: String };

  initialize() {
    this.extras = [];
    this.customizing = false;
    this.replacingRows = false;
    this.maxExtras = 20;
    this.maxChecksPerExtra = 30;
    this.maxCount = 9;
  }

  toggleCustomize() {
    this.customizing = this.element.classList.toggle("customizing");
    this.customizeButtonTarget.textContent = this.customizing
      ? "Done"
      : "Customize";
  }

  incrementCount(event) {
    this.stepCount(event, 1);
  }

  decrementCount(event) {
    this.stepCount(event, -1);
  }

  removeArea(event) {
    this.setCount(event.currentTarget.closest("[data-area-id]"), 0);
  }

  stepCount(event, delta) {
    const row = event.currentTarget.closest("[data-area-id]");
    this.setCount(row, parseInt(row.dataset.count, 10) + delta);
  }

  setCount(row, count) {
    count = Math.min(this.maxCount, Math.max(0, count));
    row.dataset.count = count;
    row.querySelector("[data-count-display]").textContent = count;
    row
      .querySelector("[data-strikeable]")
      .classList.toggle("line-through", count === 0);
    row
      .querySelector("[data-strikeable]")
      .classList.toggle("opacity-40", count === 0);
  }

  addArea() {
    if (this.extras.length >= this.maxExtras) return;

    this.extras.push({
      name: "",
      count: 1,
      checks: [],
      showChecklist: false,
      addingCheck: false,
    });
    this.renderExtras();
    this.extrasListTarget.lastElementChild
      .querySelector("[data-extra-name-input]")
      .focus();
  }

  updateExtraName(event) {
    this.extraFor(event).name = event.currentTarget.value.trim();
  }

  showExtraChecklist(event) {
    const extra = this.extraFor(event);
    extra.showChecklist = true;
    this.openCheckInput(extra);
  }

  showCheckInput(event) {
    this.openCheckInput(this.extraFor(event));
  }

  openCheckInput(extra) {
    extra.addingCheck = true;
    this.renderExtras();
    this.focusCheckInput(this.extras.indexOf(extra));
  }

  addCheck(event) {
    const extra = this.extraFor(event);
    const row = event.currentTarget.closest("[data-extra-row]");
    const input = row.querySelector("[data-extra-check-input]");
    const check = input.value.trim();
    if (!check || extra.checks.length >= this.maxChecksPerExtra) return;

    extra.checks.push(check);
    extra.addingCheck = extra.checks.length < this.maxChecksPerExtra;
    this.renderExtras();
    this.focusCheckInput(this.extras.indexOf(extra));
  }

  commitCheck(event) {
    const input = event.currentTarget;
    if (this.replacingRows || !input.isConnected) return;

    const extra = this.extraFor(event);
    const check = input.value.trim();
    if (check && extra.checks.length < this.maxChecksPerExtra) {
      extra.checks.push(check);
    }

    extra.addingCheck = false;
    extra.showChecklist = extra.checks.length > 0;
    this.renderExtras();
  }

  removeCheck(event) {
    const extra = this.extraFor(event);
    extra.checks.splice(
      parseInt(event.currentTarget.dataset.checkIndex, 10),
      1,
    );
    this.renderExtras();
  }

  incrementExtraCount(event) {
    this.stepExtraCount(event, 1);
  }

  decrementExtraCount(event) {
    this.stepExtraCount(event, -1);
  }

  removeExtraArea(event) {
    const extra = this.extraFor(event);
    extra.count = 0;
    this.renderExtras();
  }

  stepExtraCount(event, delta) {
    const extra = this.extraFor(event);
    extra.count = Math.min(this.maxCount, Math.max(0, extra.count + delta));
    this.renderExtras();
  }

  extraFor(event) {
    return this.extras[
      parseInt(
        event.currentTarget.closest("[data-extra-row]").dataset.extraIndex,
        10,
      )
    ];
  }

  focusCheckInput(index) {
    if (index === null || index < 0) return;
    const input = this.extrasListTarget.children[index]?.querySelector(
      "[data-extra-check-input]",
    );
    if (input) input.focus();
  }

  renderExtras() {
    this.replacingRows = true;
    this.extrasListTarget.replaceChildren(
      ...this.extras.map((extra, index) => this.buildExtraRow(extra, index)),
    );
    this.replacingRows = false;
  }

  buildExtraRow(extra, index) {
    const row =
      this.extraTemplateTarget.content.firstElementChild.cloneNode(true);
    row.dataset.extraIndex = index;
    row.querySelector("[data-extra-name-input]").value = extra.name;
    row.querySelector("[data-extra-count]").textContent = extra.count;

    const strikeable = row.querySelector("[data-strikeable]");
    strikeable.classList.toggle("line-through", extra.count === 0);
    strikeable.classList.toggle("opacity-40", extra.count === 0);

    const showChecklist = extra.showChecklist || extra.checks.length > 0;
    row.querySelector("[data-checklist-editor]").style.display = showChecklist
      ? ""
      : "none";
    row.querySelector("[data-add-checklist]").style.display = showChecklist
      ? "none"
      : "";
    row.querySelector("[data-check-form]").style.display = extra.addingCheck
      ? ""
      : "none";
    row.querySelector("[data-add-check]").style.display = extra.addingCheck
      ? "none"
      : "";

    const checksList = row.querySelector("[data-extra-checks]");
    extra.checks.forEach((check, checkIndex) => {
      const item = document.createElement("li");
      item.className = "flex items-center gap-2";

      const box = document.createElement("span");
      box.className = "size-3 shrink-0 rounded-[3px] border border-gray-300";

      const label = document.createElement("span");
      label.className = "flex-1";
      label.textContent = check;

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className =
        "hidden [.customizing_&]:inline text-extra-muted hover:text-red-500 cursor-pointer";
      remove.textContent = "×";
      remove.dataset.action = "template-printer#removeCheck";
      remove.dataset.checkIndex = checkIndex;

      item.append(box, label, remove);
      checksList.append(item);
    });

    return row;
  }

  openModal() {
    this.modalTarget.showModal();
  }

  closeModal() {
    this.modalTarget.close();
  }

  downloadPrint() {
    const params = new URLSearchParams();

    this.areaRowTargets.forEach((row) => {
      const count = parseInt(row.dataset.count, 10);
      if (count !== 1) params.append(`counts[${row.dataset.areaId}]`, count);
    });

    this.extras
      .filter((extra) => extra.name && extra.count > 0)
      .forEach((extra, index) => {
        params.append(`extras[${index}][name]`, extra.name);
        if (extra.count !== 1)
          params.append(`extras[${index}][count]`, extra.count);
        extra.checks.forEach((check) =>
          params.append(`extras[${index}][checks][]`, check),
        );
      });

    params.append("print", "1");

    window.open(`${this.printUrlValue}?${params.toString()}`, "_blank");
    this.closeModal();
  }
}
